/**
 * Item-identity field precedence per `rs_id`, used to dedup an incremental
 * "Buscar mais dados" result against what's already rendered (task #21220).
 *
 * Mirrors the `alias_groups`/identity-carrying fields defined in the
 * processor's `redes_sociais_key_map.py` `SECTION_MODELS` for each section
 * family — kept as a standalone frontend copy (no shared package between
 * backend and frontend builds). Sections with no stable identity field
 * defined server-side (the LinkedIn-only sections) are intentionally absent
 * here — `mergeSectionData.helper.ts` falls back to structural comparison
 * for those.
 */
const IDENTITY_LABELS_BY_RS_ID: Readonly<Record<string, readonly string[]>> = {
  // Content-type sections — share the post-like SectionModel server-side,
  // so they share the same identity fields.
  rs_tweets: ["ID do Post", "ID do Tweet", "ID da Midia"],
  rs_posts: ["ID do Post", "ID do Tweet", "ID da Midia"],
  rs_marcacoes: ["ID do Post", "ID do Tweet", "ID da Midia"],
  rs_reels: ["ID do Post", "ID do Tweet", "ID da Midia"],
  rs_stories: ["ID do Post", "ID do Tweet", "ID da Midia"],
  rs_publicacao_consultada: ["ID do Post", "ID do Tweet", "ID da Midia"],
  // Media sections — share the same identity fields.
  rs_midias_enviadas: ["ID da Midia", "ID do Post", "ID do Album"],
  rs_albuns: ["ID da Midia", "ID do Post", "ID do Album"],
  rs_destaques: ["ID do Destaque", "ID do Tweet"],
  rs_seguidores: ["ID Perfil", "Identificador primario"],
  rs_seguindo: ["ID Perfil", "Identificador primario"],
  rs_amigos: ["ID Perfil", "Identificador primario"],
  // Frente A (Interação, 2026-08-18): built directly by the backend
  // (redes_sociais_interaction_format.py), NOT the processor's canonical
  // SECTION_MODELS pipeline — so labels are the RAW lowercase Snap field
  // names (e.g. "id do comentario"), not the Title-Case labels every OTHER
  // entry here uses. Deliberately lowercase — matches this section's actual
  // wire shape exactly, verified live 2026-08-17/18.
  rs_interacao_comentarios: ["id do comentario"],
  rs_interacao_curtidas: ["identificador primario"],
};

/**
 * Identity label precedence for `rs_id`, or `[]` when no stable identity
 * field is defined for that section family (caller falls back to structural
 * dedup).
 */
export function resolveIdentityLabels(rsId: string): readonly string[] {
  return IDENTITY_LABELS_BY_RS_ID[rsId] ?? [];
}
